import groupBy from 'lodash.groupby';
import { PartialStateStore } from '../../../core/types/application-state';
import { withDefault, APP_VERSION, APP_NAME } from '../../../core/util';
import {
  appTaxableInvestmentsToProfileInvestments,
  convertToChosenInvestments,
  getResidentialAddressForEnabled,
  filterMailingAddressForEnabled,
  populateAppAdditionalCustomers,
  populateProfileAdditionalCustomers
} from '../../shared/services/util';
import {
  applicationStatusTypes,
  accountTypes,
  chargeTypes,
  parsePhone,
  setTaxIDType,
  fundingTypes,
  customerTypes,
  setRJCustomerPhones,
  getMailingAddressDifferentFlag
} from '../../shared/utils';
import {
  getFundNameByTRPCode,
  parseInvestmentsForRemittanceForm
} from '../../shared/services/check-remittance.service';
import { getFundAllocationsService } from '../../shared/services/allocation-service';
import { formatDate } from '../../shared/dateUtils';
import { StaticCoTrustee } from '../../../core/types/additional-customers';
import { DeepPartialProfileArgs } from '../../../core/types/return-journey-profile';

export const parseExistingAgreements = (
  accounts: any[] | undefined | null
): any => {
  if (!accounts) return false;
  // filter accounts with a balance more than zero
  const filteredAccounts =
    accounts && accounts.filter(account => account.balanceAsOf > 0);
  // group investments for each account
  const groupedAccounts: any = groupBy(
    filteredAccounts,
    'registrationGroupKey'
  );
  return groupedAccounts;
};

export const convertTrustAppDataToProfileData = (
  store: PartialStateStore
): DeepPartialProfileArgs => {
  const {
    app,
    progressStepText,
    chosenInvestments,
    additionalCustomers
  } = store;

  const investmentAllocations = appTaxableInvestmentsToProfileInvestments(
    chosenInvestments
  );
  return {
    applicationID: withDefault(app?.profileApplicationID, ''),
    emailAddress: app?.trustee?.primaryEmailAddress,
    applicationStatus: withDefault(
      app?.applicationStatus,
      applicationStatusTypes.IN_PROGRESS
    ),
    investmentAllocations,
    // ToDo: Below types need to be added and pulled in from store w.r.t current flow
    accountType: withDefault(store?.accountType, accountTypes.TRUST),
    fundingType: withDefault(app?.fundingMethodType, fundingTypes.CHECK),
    chargeType: withDefault(app?.chargeType, chargeTypes.NEW),
    investAmount: withDefault(store?.amount, 0),
    firstName: app?.trustee?.name?.firstName,
    lastName: app?.trustee?.name?.lastName,
    middleName: withDefault(app?.trustee?.name?.middleName, ''),
    residentialAddress: {
      street1: app?.trustee?.residentialAddress?.street1,
      street2: app?.trustee?.residentialAddress?.street2,
      state: app?.trustee?.residentialAddress?.state,
      zip: app?.trustee?.residentialAddress?.zip,
      city: app?.trustee?.residentialAddress?.city
    },
    mailingAddress: {
      street1: app?.trustee?.mailingAddress?.street1,
      street2: app?.trustee?.mailingAddress?.street2,
      state: app?.trustee?.mailingAddress?.state,
      zip: app?.trustee?.mailingAddress?.zip,
      city: app?.trustee?.mailingAddress?.city
    },
    citizenship: withDefault(app?.trustee?.citizenship, 'UNKNOWN'),
    dateOfBirth: app?.trustee?.dob,
    taxID: withDefault(app?.trustee?.taxID, ''),
    phoneNumbers:
      app?.trustee?.phones?.map(({ areaCode, exchange, lastFour, type }) => ({
        phoneNumber: `(${areaCode}) ${exchange}-${lastFour}`,
        phoneNumberType: type
      })) || [],
    trust: app?.trust,
    additionalCustomers: populateProfileAdditionalCustomers(
      additionalCustomers
    ),
    investmentSelectionPath: app?.investmentMethod,
    residentialAddressDifferentFromMailing: withDefault(
      store?.mailingAddressDifferent,
      false
    ),
    termsIDs: withDefault(
      app?.termsIDs?.map(id => id?.toString()),
      []
    ),
    consumerVersion: APP_VERSION,
    consumerName: withDefault(store?.consumerName, APP_NAME),
    progressStepText,
    progressStep: withDefault(store?.progressStep, 0)
  };
};

export const populateCoTrustees = (
  additionalCustomers: any
): StaticCoTrustee[] =>
  additionalCustomers.map(customer =>
    customer.customerType === customerTypes.TRUSTEE
      ? {
          name: {
            firstName: customer.firstName,
            middleName: withDefault(customer.middleName, ''),
            lastName: customer.lastName
          },
          citizenship: customer.citizenship,
          taxID: customer.taxID,
          taxIDType: setTaxIDType(customer.citizenship),
          dob: customer.dateOfBirth,
          residentialAddress: customer.residentialAddress
        }
      : undefined
  );

export const convertProfileDataToTrustAppData = async (
  profile: DeepPartialProfileArgs,
  store: PartialStateStore
): Promise<PartialStateStore> => {
  const { app, enabled } = store;
  const phones = setRJCustomerPhones(
    profile?.phoneNumbers,
    app?.trustee?.phones
  );
  const mailingAddress = withDefault(
    app?.trustee?.mailingAddress,
    app?.customer?.mailingAddress
  );
  const residentialAddress = withDefault(
    app?.trustee?.residentialAddress,
    app?.customer?.residentialAddress
  );

  return {
    accountType: profile?.accountType,
    amount: profile?.investAmount,
    app: {
      trustee: enabled
        ? {
            ...(app?.trustee || app?.customer),
            citizenship: profile?.citizenship,
            taxIDType: setTaxIDType(profile?.citizenship),
            residentialAddress: getResidentialAddressForEnabled(
              residentialAddress,
              profile?.residentialAddress
            ),
            mailingAddress: filterMailingAddressForEnabled(mailingAddress),
            primaryEmailAddress: withDefault(
              profile?.emailAddress,
              app?.trustee?.primaryEmailAddress
            ),
            phones
          }
        : {
            name: {
              firstName: profile?.firstName,
              middleName: withDefault(profile?.middleName, ''),
              lastName: profile?.lastName
            },
            citizenship: profile?.citizenship,
            dob: profile?.dateOfBirth,
            taxID: profile?.taxID,
            taxIDType: setTaxIDType(profile?.citizenship),
            residentialAddress: {
              street1: profile?.residentialAddress?.street1,
              street2: profile?.residentialAddress?.street2,
              state: profile?.residentialAddress?.state,
              zip: profile?.residentialAddress?.zip,
              city: profile?.residentialAddress?.city
            },
            mailingAddress: {
              street1: profile?.mailingAddress?.street1,
              street2: profile?.mailingAddress?.street2,
              state: profile?.mailingAddress?.state,
              zip: profile?.mailingAddress?.zip,
              city: profile?.mailingAddress?.city
            },
            primaryEmailAddress: profile?.emailAddress,
            // [{phoneNumberType: string, phoneNumber: string}]
            // needs to be converted to ->
            // [{ areaCode: string, exchange: string, lastFour: string, type: string }]
            //
            // TODO [2020-06-20]: This is a big pain why can't backend use same format
            phones: profile?.phoneNumbers?.map(
              ({ phoneNumberType: type = '', phoneNumber = '' }) => ({
                ...parsePhone(phoneNumber),
                type
              })
            )
          },
      trust: profile?.trust,
      ...(profile?.additionalCustomers &&
        profile.additionalCustomers.length && {
          coTrustees: populateCoTrustees(profile.additionalCustomers)
        }),
      newInvestments: profile?.investmentAllocations?.map(
        ({ ticker, trpCode, fundingAmount }) => ({
          ticker,
          trpCode,
          fundingAmount
        })
      ),
      investmentMethod: profile?.investmentSelectionPath,
      profileApplicationID: profile?.applicationID,
      termsIDs: profile?.termsIDs,
      fundingMethodType: withDefault(profile?.fundingType, fundingTypes.CHECK)
    },
    chosenInvestments: await convertToChosenInvestments(
      profile?.investmentAllocations,
      profile?.investmentSelectionPath
    ),
    additionalCustomers: populateAppAdditionalCustomers(
      profile.additionalCustomers
    ),
    mailingAddressDifferent: getMailingAddressDifferentFlag(
      enabled,
      store,
      profile
    ),
    progressStepText: profile?.progressStepText,
    consumerVersion: profile?.consumerVersion,
    consumerName: withDefault(profile?.consumerName, APP_NAME)
  };
};

export const convertAppDataToTrustFundFormData = (
  store: PartialStateStore
): any => {
  const { loadedData, chosenInvestments } = store;

  // DE53208 Filter out Existing Agreements with non zero investment amount
  const filterZeroBalExistingInvestments = chosenInvestments.filter(
    estInvAmount => estInvAmount.balanceAsOf !== '0'
  );

  const allocations = filterZeroBalExistingInvestments.map(acc => {
    return {
      agreementID: acc.agreementID,
      fundName: acc.fundName || acc.name,
      fundingPortion: acc.percent === 100 ? 'ALL' : 'PARTIAL',
      allocationUnit: 'DOLLAR',
      ...(acc.percent !== 100 && {
        partialAmount: parseFloat(acc.balanceAsOf)
      })
    };
  });
  const receivingAccount = {
    accountNumber: loadedData.accounts[0].accountNumber,
    fundName:
      getFundNameByTRPCode(
        loadedData.accounts[0].investmentVehicle.trpCode,
        filterZeroBalExistingInvestments
      ) || loadedData.accounts[0].investmentVehicle.trpCode
  };
  return {
    receivingAccount,
    originatingAccounts: {
      changeAllMatchingAccounts: !loadedData.partialExistingFunds,
      allocations
    }
  };
};

export const convertAppDataToTrustRemittanceFormData = (
  store: PartialStateStore,
  fundDetails
): any => {
  const {
    accountType,
    app,
    chosenInvestments,
    loadedData: { accounts = [] }
  } = store;

  const payloadArgs = {
    accountType,
    customer: {
      firstName: app?.trustee?.name?.firstName,
      middleName: app?.trustee?.name?.middleName,
      lastName: app?.trustee?.name?.lastName
    },
    trust: {
      name: app?.trust?.name
    },
    address: {
      street1: app?.trustee?.mailingAddress?.street1,
      city: app?.trustee?.mailingAddress?.city,
      state: app?.trustee?.mailingAddress?.state,
      zip: app?.trustee?.mailingAddress?.zip
    },
    fundSelections: parseInvestmentsForRemittanceForm(
      chosenInvestments,
      fundDetails,
      accounts
    )
  };
  return payloadArgs;
};

export const loadInvestmentDetails = async (newInvestments): Promise<any> => {
  const tickers = newInvestments.map(f => f.ticker);
  const fundDetails = await getFundAllocationsService({ tickers });
  return fundDetails;
};

export const addAdditionalCustomers = (
  coTrustee: boolean,
  people: any[] = []
): any =>
  people.length
    ? people.map(
        ({
          coTrusteeFirstName,
          coTrusteeMiddleName = '',
          coTrusteeLastName,
          coTrusteeDob,
          coTrusteeSsn = '',
          coTrusteeCitizenship
        }) => ({
          ...(!coTrustee && { customerType: 'TRUSTEE' }),
          name: {
            firstName: coTrusteeFirstName,
            middleName: withDefault(coTrusteeMiddleName, ''),
            lastName: coTrusteeLastName
          },
          dob: formatDate(coTrusteeDob, 'MM/DD/YYYY', ''),
          taxID: coTrusteeSsn.replace(/[^*0-9]/g, ''),
          ...(coTrustee && { taxIDType: setTaxIDType(coTrusteeCitizenship) }),
          citizenship: coTrusteeCitizenship
        })
      )
    : [];

export const checkTrustPartialAllocation = (agreements): boolean =>
  agreements.some(({ balanceAsOf }) => balanceAsOf === '0');
